import { Moon, Sun } from 'lucide-react'
import { useTheme } from '../app/ThemeProvider'
import { Icon } from '../platform/icons/Icon'
import { AccountMenu } from '../auth/AccountMenu'
import { NotificationsBell } from '../notifications/NotificationsDrawer'
import { Button } from '../platform/primitives/Button'
import { BackendBadge } from './BackendBadge'
import { TierBadge } from './TierBadge'
import { useBreakpoint } from '../platform/responsive/useBreakpoint'
import { usePageMetaValue } from './PageMeta'
import './shell.css'

/**
 * Breadcrumb and appearance toggle.
 *
 * **The page name is an `<h1>`.** The plan renders the whole breadcrumb as three
 * spans, which would leave every route in the app without a heading — the first
 * thing a screen-reader user navigates by. Semantics and type scale are
 * independent, so it is styled down to breadcrumb size in shell.css.
 *
 * **⌘K search is not here.** The plan renders a "Search… ⌘K" button with no
 * handler. A live-looking inert control is worse than an absent one, and
 * CLAUDE.md's alternative — a disabled control that states its reason — has
 * nowhere to put that reason in a top bar. Search arrives with its own command
 * palette surface (#110).
 *
 * **Notifications are here now** (#191). The bell owns its own open state, its
 * own trigger ref and its own query, so this file gains one element rather
 * than a feature: a top bar that held the drawer's state would be the reason
 * every route re-rendered when a notification was marked read.
 */
/** The breadcrumb's h1 is the document's only heading, so `ChromeLayout` points
 *  `<main aria-labelledby>` at it rather than leaving the landmark unnamed. */
export const PAGE_TITLE_ID = 'shell-page-title'

export function TopBar({ section, page }: { section: string; page: string }) {
  const { resolved, setPreference } = useTheme()
  const meta = usePageMetaValue()
  const phone = useBreakpoint() === 'phone'

  // From `resolved`, NOT `preference`. On the default preference — 'system' —
  // with a dark OS, `preference === 'dark'` is false, so the plan's version
  // offers "Switch to dark" while the screen is already dark and pressing it
  // changes nothing visible. What the user sees is `resolved`; the offer has to
  // be the opposite of that.
  const next = resolved === 'dark' ? 'light' : 'dark'

  return (
    <header className="topbar">
      {/* Trail and count STACKED, not spread across the bar. The count was a
          flex item of its own, so it drifted to the middle of a 1500px bar with
          nothing tying it to the title it describes — Sushil, 2026-08-21. A
          column wrapper is what makes them read as one identity block.

          The `<p>` stays OUTSIDE the breadcrumb `<nav>` even inside the
          wrapper: it is descriptive text, not a destination, and a landmark
          labelled "Breadcrumb" must not announce it as part of the trail.
          Rendered only when a page publishes something, so pages that publish
          nothing leave no empty slot. */}
      <div className="topbar__identity">
        <nav aria-label="Breadcrumb" className={phone ? 'topbar__crumb topbar__crumb--phone' : 'topbar__crumb'}>
          {/* On a phone the bar has room for the title alone: with the section
              in front, a long one ("Settings", "Organise") ate the line and the
              title never showed. The trail stays in the DOM for assistive
              technology. */}
          <span className={phone ? 'sr-only' : undefined}>
            {section} <span className="topbar__separator">/</span>{' '}
          </span>
          <h1 id={PAGE_TITLE_ID} className="topbar__page">
            {page}
          </h1>
        </nav>
        {meta !== null && <p className="topbar__meta">{meta}</p>}
      </div>
      {/* #449 and #477. Which API and which entitlement tier this page load
          uses, in every build except production. The MODE check is inline so
          Vite folds it to `false` in production and neither badge is ever
          emitted. One wrapper carries the shared `margin-left: auto` — see
          `.topbar__badges` — so the pair pushes right as a unit instead of
          each badge fighting the other for the same auto margin. */}
      {/* Not on a phone: two dev pills left the title 34px of a 375px bar. */}
      {import.meta.env.MODE !== 'production' && !phone && (
        <div className="topbar__badges">
          <BackendBadge />
          <TierBadge />
        </div>
      )}
      {/* Flex rather than a grid: the account block is absent before there is a
          session, and a grid would hold its column open. */}
      <div className="topbar__actions">
        {/* Icon-only, as the design draws it — #193.

            The visible text "Switch to dark" was both the label AND the state
            announcement. Dropping to an icon keeps the name on `aria-label`,
            so nothing is lost to assistive technology; the icon shows the
            theme you would MOVE TO, matching the name, because an icon
            showing the current state and a label describing the next one is
            the classic toggle ambiguity. */}
        <Button
          variant="outline"
          onClick={() => setPreference(next, 'top_bar')}
          aria-label={`Switch to ${next}`}
        >
          <Icon glyph={next === 'dark' ? Moon : Sun} />
        </Button>
        {/* Appearance, notifications, account — the order the design draws. */}
        <NotificationsBell />
        <AccountMenu />
      </div>
    </header>
  )
}
